import {
  doc,
  DocumentReference,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  runTransaction,
  Transaction,
  updateDoc,
  where,
} from "firebase/firestore";

import { BinStatusEnum, ServiceTaskTypeEnum } from "@/domain/models/common";
import { FirestoreDataType } from "@/domain/models/firebase";
import { ServicesType, TasksType } from "@/domain/models/job";
import { auth, firestore } from "@/firebase.config";
import { TASK_TIME_DURATION } from "@/src/domain/constants";
import { FunctionsEnum } from "@/src/domain/functions";
import {
  AssignTaskToDriverParamsType,
  JobServiceTaskBaseType,
  StartTaskFunctionRequestType,
  TaskFiltersType,
  UpdateConnectedTaskType,
  UpdateTaskCleanupParamsType,
  UpdateTaskDueAtParamsType,
} from "@/src/domain/task";
import { dateAsDayjs, generateFirebaseTimestamp } from "@/src/utils/date";
import { logger } from "@/src/utils/logger";
import { getBinRentalStatus } from "@/src/utils/strings";

import { callFunction } from "../functions";
import { BinApi } from "./BinApi";
import { JobApi } from "./JobApi";
import { ServiceApi } from "./ServiceApi";
import { SpikeSosDb } from "./SpikesosDb";

export const TaskApi = {
  getRef: ({ jobId, serviceId, taskId }: JobServiceTaskBaseType) => {
    return doc(
      SpikeSosDb.jobs,
      `${jobId}/services/${serviceId}/tasks/${taskId}`,
    ) as DocumentReference<FirestoreDataType<TasksType>>;
  },
  listTasks: async ({ filters }: { filters?: TaskFiltersType }) => {
    try {
      const tasksCollection = SpikeSosDb.tasks;

      const constraints = [];

      if (filters?.dueAtStart) {
        constraints.push(where("dueAt", ">=", filters.dueAtStart));
      }

      if (filters?.dueAtEnd) {
        constraints.push(where("dueAt", "<", filters.dueAtEnd));
      }

      if (filters?.completed !== undefined) {
        constraints.push(where("completed", "==", filters.completed));
      }

      if (filters?.taskIds) {
        constraints.push(where("taskId", "in", filters.taskIds));
      }

      if (filters?.assignedTo) {
        constraints.push(where("assignedTo", "==", filters.assignedTo));
      }

      if (filters?.started !== undefined) {
        constraints.push(where("started", "==", filters.started));
      }

      if (filters?.needToDump !== undefined) {
        constraints.push(where("needToDump", "==", filters.needToDump));
      }

      if (filters?.hasDumped !== undefined) {
        constraints.push(where("hasDumped", "==", filters.hasDumped));
      }

      if (filters?.dueAtNotNull) {
        constraints.push(where("dueAt", "!=", null));
      }

      const tasksQuery = query(tasksCollection, ...constraints);

      // Execute the query and return the results
      const querySnapshot = await getDocs(tasksQuery);
      const tasks = querySnapshot.docs.map(doc => ({
        id: doc.id as ServiceTaskTypeEnum,
        ...doc.data(),
      }));

      return tasks;
    } catch (error) {
      logger.error("Error fetching tasks", error);

      throw error;
    }
  },
  countTasks: async (filters?: TaskFiltersType) => {
    try {
      const tasksCollection = SpikeSosDb.tasks;

      const constraints = [];

      if (filters?.dueAtStart) {
        constraints.push(where("dueAt", ">=", filters.dueAtStart));
      }

      if (filters?.dueAtEnd) {
        constraints.push(where("dueAt", "<", filters.dueAtEnd));
      }

      if (filters?.completed) {
        constraints.push(where("completed", "==", filters.completed));
      }

      if (filters?.taskIds) {
        constraints.push(where("taskId", "in", filters.taskIds));
      }

      if (filters?.assignedTo) {
        constraints.push(where("assignedTo", "==", filters.assignedTo));
      }

      const tasksQuery = query(tasksCollection, ...constraints);

      // Execute the query and return the count of results
      const querySnapshot = await getCountFromServer(tasksQuery);
      const tasksCount = querySnapshot.data().count;

      return tasksCount;
    } catch (error) {
      logger.error("Error fetching tasks count", error);

      throw error;
    }
  },
  assignTaskToDriver: async ({
    jobId,
    serviceId,
    taskId,
    assignedTo,
    selectedDate,
  }: AssignTaskToDriverParamsType) => {
    try {
      if (!jobId || !serviceId || !taskId) {
        throw new Error(
          "Missing required parameters to assign task from driver",
        );
      }

      // Default due at time is 6am
      let dueAtBase = dateAsDayjs(selectedDate).startOf("day").add(6, "hour");

      if (assignedTo) {
        // Get the driver's current list of tasks
        const tasks = await TaskApi.listTasks({
          filters: {
            assignedTo,
            dueAtStart: dateAsDayjs(selectedDate).startOf("day").toDate(),
            dueAtEnd: dateAsDayjs(selectedDate).endOf("day").toDate(),
          },
        });

        if (tasks.length) {
          // Get the latest task
          const latestTask = tasks.reduce((prev, current) =>
            prev.dueAt > current.dueAt ? prev : current,
          );

          if (latestTask.dueAt) {
            // Add 90 minutes to the latest task's due at time
            dueAtBase = dateAsDayjs(latestTask.dueAt.toDate()).add(
              TASK_TIME_DURATION,
              "minute",
            );
          }
        }
      }

      const dueAt = generateFirebaseTimestamp(dueAtBase.toDate());
      const end = generateFirebaseTimestamp(
        dateAsDayjs(dueAtBase.toDate())
          .add(TASK_TIME_DURATION, "minute")
          .toDate(),
      );

      const jobRef = doc(SpikeSosDb.jobs, jobId);
      const serviceRef = doc(SpikeSosDb.jobs, `${jobId}/services/${serviceId}`);
      const taskRef = doc(
        SpikeSosDb.jobs,
        `${jobId}/services/${serviceId}/tasks/${taskId}`,
      );

      await updateDoc(jobRef, { assignedTo, dueAt, end });
      await updateDoc(serviceRef, { assignedTo, dueAt, end });
      await updateDoc(taskRef, { assignedTo, dueAt, end });

      return { jobId, serviceId, taskId, assignedTo, selectedDate };
    } catch (error) {
      logger.error("Error assigning task", error);

      throw error;
    }
  },
  unassignTaskFromDriver: async ({
    jobId,
    serviceId,
    taskId,
  }: JobServiceTaskBaseType) => {
    try {
      if (!jobId || !serviceId || !taskId) {
        throw new Error(
          "Missing required parameters to unassign task from driver",
        );
      }

      const jobRef = doc(SpikeSosDb.jobs, jobId);
      const serviceRef = doc(SpikeSosDb.jobs, `${jobId}/services/${serviceId}`);
      const taskRef = doc(
        SpikeSosDb.jobs,
        `${jobId}/services/${serviceId}/tasks/${taskId}`,
      );

      await updateDoc(jobRef, { assignedTo: "" });
      await updateDoc(serviceRef, { assignedTo: "" });
      await updateDoc(taskRef, { assignedTo: "" });

      return { jobId, serviceId, taskId };
    } catch (error) {
      logger.error("Error unassigning task", error);

      throw error;
    }
  },
  updateTaskDueAt: async ({
    jobId,
    serviceId,
    taskId,
    dueAt,
  }: UpdateTaskDueAtParamsType) => {
    try {
      if (!jobId || !serviceId || !taskId || !dueAt) {
        throw new Error("Missing required parameters");
      }

      const jobRef = doc(SpikeSosDb.jobs, jobId);
      const serviceRef = doc(SpikeSosDb.jobs, `${jobId}/services/${serviceId}`);
      const taskRef = doc(
        SpikeSosDb.jobs,
        `${jobId}/services/${serviceId}/tasks/${taskId}`,
      );

      const end = generateFirebaseTimestamp(
        dateAsDayjs(dueAt.toDate()).add(90, "minute").toDate(),
      );

      const date = generateFirebaseTimestamp(dueAt.toDate());

      await updateDoc(jobRef, { startDate: date, date, dueAt, end });
      await updateDoc(serviceRef, { date, dueAt, end });
      await updateDoc(taskRef, { dueAt, end });

      return { jobId, serviceId, taskId, dueAt };
    } catch (error) {
      logger.error("Error updating task due at", error);

      throw error;
    }
  },
  updateTaskCleanupType: async ({
    jobId,
    serviceId,
    taskId,
    cleanupType,
  }: UpdateTaskCleanupParamsType) => {
    try {
      if (!jobId || !serviceId || !taskId || !cleanupType) {
        throw new Error("Missing required parameters");
      }
      const jobRef = doc(SpikeSosDb.jobs, jobId);

      const serviceRef = doc(SpikeSosDb.jobs, `${jobId}/services/${serviceId}`);
      const taskRef = doc(
        SpikeSosDb.jobs,
        `${jobId}/services/${serviceId}/tasks/${taskId}`,
      );
      await updateDoc(jobRef, { cleanupType });
      await updateDoc(serviceRef, { cleanupType });
      await updateDoc(taskRef, { cleanupType });

      return { jobId, serviceId, taskId, cleanupType };
    } catch (error) {
      logger.error("Error updating task due at", error);

      throw error;
    }
  },
  getTask: async ({ jobId, serviceId, taskId }: JobServiceTaskBaseType) => {
    try {
      if (!jobId || !serviceId || !taskId) {
        throw new Error("Missing required parameters");
      }

      const docRef = doc(
        SpikeSosDb.jobs,
        `${jobId}/services/${serviceId}/tasks/${taskId}`,
      );
      const document = await getDoc(docRef);

      if (!document.exists()) {
        throw new Error("Task not found");
      }

      return {
        id: document.id,
        ...(document.data() as unknown as FirestoreDataType<TasksType>),
      };
    } catch (error) {
      logger.error("Error fetching task", error);

      throw error;
    }
  },
  getTaskByRef: async (
    taskRef: DocumentReference<FirestoreDataType<TasksType>>,
  ) => {
    try {
      const document = await getDoc(taskRef);

      if (!document.exists()) {
        throw new Error("Task not found");
      }

      return {
        id: document.id,
        ...(document.data() as unknown as FirestoreDataType<TasksType>),
      };
    } catch (error) {
      logger.error("Error fetching task by ref", error);

      throw error;
    }
  },
  startTask: async (params: StartTaskFunctionRequestType) => {
    try {
      const result = await callFunction({
        func: FunctionsEnum.StartTask,
        opts: { body: params },
      });

      return result.data;
    } catch (error) {
      logger.error("Error starting task", error);
      throw error;
    }
  },
  updateTask: async (params: Partial<TasksType>) => {
    try {
      const { jobId, serviceId, taskId, ...rest } = params;

      if (!jobId || !serviceId || !taskId) {
        throw new Error("Missing required parameters");
      }

      const taskRef = TaskApi.getRef({
        jobId,
        serviceId,
        taskId,
      });

      await updateDoc(taskRef, rest);

      return params;
    } catch (error) {
      logger.error("Error updating task", error);
      throw error;
    }
  },
  updateConnectedTask: async ({
    jobId,
    serviceId,
    taskId,
    connectedTask,
  }: UpdateConnectedTaskType) => {
    try {
      const jobRef = doc(SpikeSosDb.jobs, jobId);
      const serviceRef = doc(SpikeSosDb.jobs, `${jobId}/services/${serviceId}`);
      const taskRef = doc(
        SpikeSosDb.jobs,
        `${jobId}/services/${serviceId}/tasks/${taskId}`,
      );

      await updateDoc(jobRef, { connectedTask });
      await updateDoc(taskRef, { connectedTask });
      await updateDoc(serviceRef, { connectedTask });

      return { jobId, serviceId, taskId, connectedTask };
    } catch (error) {
      logger.error("Error updating connected task", error);
      throw error;
    }
  },
  completeTask: async ({
    jobId,
    serviceId,
    taskId,
  }: JobServiceTaskBaseType) => {
    try {
      const now = generateFirebaseTimestamp();

      const currentUser = auth.currentUser;

      const taskRef = TaskApi.getRef({ jobId, serviceId, taskId });
      const taskData = await TaskApi.getTask({ jobId, serviceId, taskId });

      const taskProps: Partial<TasksType> = {
        completed: true,
        completedUpdatedAt: now,
      };

      // If it's completing a dump task, we need to change the fields related to dumping process
      if (taskData.needToDump && !taskData.hasDumped) {
        taskProps.hasDumped = true;
        taskProps.dumpedAt = generateFirebaseTimestamp();
      }

      await updateDoc(taskRef, taskProps);

      if (taskId !== ServiceTaskTypeEnum.DROPBIN) {
        const serviceRef = ServiceApi.getRef({ jobId, serviceId });
        const serviceData = await ServiceApi.getService({ jobId, serviceId });

        const serviceProps: Partial<ServicesType> = {
          completed: true,
          completedUpdatedAt: now,
          driver: currentUser?.displayName ?? "",
        };

        // If it's a dump task, we need to change the fields related to dumping process
        if (serviceData.needToDump && !serviceData.hasDumped) {
          serviceProps.hasDumped = true;
          serviceProps.dumpedAt = generateFirebaseTimestamp();
        }

        await updateDoc(serviceRef, serviceProps);

        if (
          taskId !== ServiceTaskTypeEnum.WAITANDLOADBIN &&
          taskId !== ServiceTaskTypeEnum.DUMPANDRETURNBIN &&
          taskId !== ServiceTaskTypeEnum.JUNKREMOVAL
        ) {
          const serviceData = await ServiceApi.getService({ jobId, serviceId });

          if (serviceData?.connectedTask) {
            const connectedServiceRef = ServiceApi.getRef({
              jobId: serviceData.connectedTask.jobId,
              serviceId: serviceData.connectedTask.id,
            });

            const connectedServiceDoc = await getDoc(connectedServiceRef);

            if (connectedServiceDoc.exists()) {
              const connectedServiceData = connectedServiceDoc.data();

              await updateDoc(connectedServiceRef, {
                completed: true,
                completedUpdatedAt: now,
                assignedBin: {
                  ...connectedServiceData?.assignedBin,
                  status: BinStatusEnum.AVAILABLE,
                },
              });
            }
          }
        }

        // If it's a Pick Up, Exchange, or Dump and Return Bin task
        // we need to check if the bin is overdue
        if (
          taskId === ServiceTaskTypeEnum.PICKUPBIN ||
          taskId === ServiceTaskTypeEnum.EXCHANGEBIN ||
          taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN
        ) {
          const binId = serviceData.connectedTask?.assignedBin?.id;

          if (binId) {
            const binData = await BinApi.getBin(binId);
            if (binData) {
              const binRentalStatus = getBinRentalStatus(binData);
              if (binRentalStatus.isOverdue) {
                const overdueDays = binRentalStatus.overdueDays;

                await JobApi.updateJob({
                  jobId,
                  binOverdueDays: overdueDays,
                });
              }
            }
          }
        }

        if (
          taskId !== ServiceTaskTypeEnum.DUMPANDRETURNBIN &&
          taskId !== ServiceTaskTypeEnum.JUNKREMOVAL
        ) {
          const serviceData = await ServiceApi.getService({ jobId, serviceId });

          if (serviceData) {
            let binId;

            if (taskId === ServiceTaskTypeEnum.WAITANDLOADBIN) {
              binId = serviceData.assignedBin?.id;
            } else {
              binId = serviceData.connectedTask?.assignedBin?.id;
            }

            if (binId) {
              await BinApi.updateBin({
                id: binId,
                currentJob: null,
                currentService: null,
                currentData: null,
                status: BinStatusEnum.AVAILABLE,
              });
            } else {
              logger.error("TaskApi.completeTask", "No bin ID found");
            }
          } else {
            logger.error("TaskApi.completeTask", "Can't find service data");
          }
        }

        // If it's a dumpandreturnbin task, we need to update the bin status dropAt to the current date
        if (taskId === ServiceTaskTypeEnum.DUMPANDRETURNBIN) {
          const serviceData = await ServiceApi.getService({ jobId, serviceId });

          const binId = serviceData.connectedTask?.assignedBin?.id;

          if (binId) {
            // Get bin's current data object
            const binData = await BinApi.getBin(binId);
            const currentData = binData?.currentData;

            if (currentData) {
              // Update the dropAt date to the current date
              currentData.dropAt = generateFirebaseTimestamp();

              // Update the bin's current data object
              await BinApi.updateBin({
                id: binId,
                currentData,
              });
            }
          }
        }
      }

      return { jobId, serviceId, taskId };
    } catch (error) {
      logger.error("Error completing task", error);
      throw error;
    }
  },
  updateTasksInTransaction: async ({
    tasksToUpdate,
    taskParams,
  }: {
    tasksToUpdate: DocumentReference<FirestoreDataType<TasksType>>[];
    taskParams: Partial<TasksType>;
  }) => {
    try {
      await runTransaction(firestore, async transaction => {
        const promises: Transaction[] = [];

        tasksToUpdate.forEach(taskRef => {
          const taskTransaction = transaction.update(taskRef, taskParams);

          promises.push(taskTransaction);
        });

        return Promise.allSettled(promises);
      });

      return { updatedTasks: tasksToUpdate, taskParams };
    } catch (error) {
      logger.error("Error updating tasks in transaction", error);
      throw error;
    }
  },
};
